import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import DataElementsContext from './DataElementsContext'
import { useAuth } from '@/hooks/useAuth'
import { getAllDataElements } from '@/services/submittalService'
import { tokenService } from '@/services/tokenService'

const STORAGE_KEY = 'data_elements'

const safeGet = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const safeSet = value => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    /* quota exceeded or storage blocked */
  }
}

const safeRemove = () => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

export const DataElementsProvider = ({ children }) => {
  const { consultant } = useAuth()
  const [dataElements, setDataElements] = useState(() => {
    const user = tokenService.getUser()
    return user?.email ? safeGet() : null
  })
  const [isLoading, setIsLoading] = useState(() => {
    const user = tokenService.getUser()
    if (!user?.email) return false
    return !safeGet()
  })
  const [error, setError] = useState(null)
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (!consultant) {
      safeRemove()
      setDataElements(null)
      setIsLoading(false)
      setError(null)
      fetchedRef.current = false
      return
    }

    if (fetchedRef.current) return

    const cached = safeGet()
    if (cached) {
      setDataElements(cached)
      fetchedRef.current = true
      return
    }

    const fetch = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await getAllDataElements()
        setDataElements(data)
        safeSet(data)
        fetchedRef.current = true
      } catch (err) {
        setError(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetch()
  }, [consultant])

  const refreshDataElements = async () => {
    if (!consultant) return
    setIsLoading(true)
    setError(null)
    try {
      const data = await getAllDataElements()
      setDataElements(data)
      safeSet(data)
      fetchedRef.current = true
    } catch (err) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const clearDataElements = () => {
    safeRemove()
    setDataElements(null)
    setError(null)
    fetchedRef.current = false
  }

  return (
    <DataElementsContext.Provider
      value={{ dataElements, isLoading, error, refreshDataElements, clearDataElements }}
    >
      {children}
    </DataElementsContext.Provider>
  )
}

DataElementsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
